import { AnalyticsSummary } from '../services/analytics';

interface AnalyticsDashboardProps {
  summary: AnalyticsSummary | null;
  loading: boolean;
  error: string | null;
}

function SkeletonCard() {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 animate-pulse">
      <div className="h-3 w-20 bg-gray-200 rounded mb-3" />
      <div className="h-7 w-12 bg-gray-300 rounded" />
    </div>
  );
}

function SkeletonChart() {
  const SKELETON_BAR_HEIGHTS = [60, 35, 80, 50, 95, 45, 70];
  return (
    <div className="mt-4">
      <div className="h-3 w-20 bg-gray-200 rounded mb-3 animate-pulse" />
      <div className="flex items-end gap-2 h-36 pt-5 border-b border-gray-200">
        {SKELETON_BAR_HEIGHTS.map((pct, i) => (
          <div
            key={i}
            className="flex-1 flex flex-col items-center h-full justify-end animate-pulse"
          >
            <div className="w-full bg-gray-200 rounded-t" style={{ height: `${pct}%` }} />
            <div className="mt-2 h-2 w-6 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

export function AnalyticsDashboard({ summary, loading, error }: AnalyticsDashboardProps) {
  if (loading) {
    return (
      <section className="bg-white rounded-xl p-6 mb-6 shadow-sm">
        <h3 className="text-base font-bold mb-3">Analytics (Last 7 Days)</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
        <SkeletonChart />
      </section>
    );
  }

  if (error) {
    return (
      <section className="bg-white rounded-xl p-6 mb-6 shadow-sm">
        <h3 className="text-base font-bold mb-3">Analytics (Last 7 Days)</h3>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">
          <strong>Analytics unavailable:</strong> {error} Refresh the page to try again.
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6 opacity-40">
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
          <SkeletonCard />
        </div>
        <div className="opacity-40">
          <SkeletonChart />
        </div>
      </section>
    );
  }

  if (!summary) return null;

  const uniqueByDate = (summary.uniqueViewsPerDay ?? []).reduce<Record<string, number>>(
    (acc, item) => {
      acc[item.date] = item.count;
      return acc;
    },
    {},
  );

  const chartData = (summary.viewsPerDay ?? []).map((item) => ({
    date: item.date,
    views: item.count,
    unique: uniqueByDate[item.date] ?? 0,
    label: new Date(item.date).toLocaleDateString(undefined, { weekday: 'short' }),
  }));

  const maxViews = Math.max(...chartData.map((d) => d.views), 1);

  const totalUniqueViews = (summary.uniqueViewsPerDay ?? []).reduce((sum, d) => sum + d.count, 0);

  return (
    <section className="bg-white rounded-xl p-6 mb-6 shadow-sm">
      <h3 className="text-base font-bold mb-3">Analytics (Last 7 Days)</h3>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <span className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
            Total Events
          </span>
          <span className="block text-2xl font-bold text-gray-900">{summary.totalEvents}</span>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <span className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
            Subscribers
          </span>
          <span className="block text-2xl font-bold text-gray-900">{summary.subscriberCount}</span>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <span className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
            Page Views
          </span>
          <span className="block text-2xl font-bold text-gray-900">
            {summary.totalPageViews ?? 0}
          </span>
        </div>
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <span className="block text-xs text-gray-500 uppercase tracking-wide mb-1">
            Unique Visitors
          </span>
          <span className="block text-2xl font-bold text-gray-900">{totalUniqueViews}</span>
        </div>
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-xs font-semibold text-gray-600">Page Views</h4>
          <div className="flex items-center gap-3">
            <span
              className="flex items-center gap-1 text-[10px] text-gray-500"
              aria-label="Total page views"
            >
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm bg-brand-500"
                aria-hidden="true"
              />
              Total
            </span>
            <span
              className="flex items-center gap-1 text-[10px] text-gray-500"
              aria-label="Unique visitors"
            >
              <span
                className="inline-block w-2.5 h-2.5 rounded-sm bg-teal-400"
                aria-hidden="true"
              />
              Unique
            </span>
          </div>
        </div>
        <div className="flex items-end gap-1 h-36 pt-5 border-b border-gray-200">
          {chartData.map((day) => (
            <div key={day.date} className="flex-1 flex flex-col items-center h-full justify-end">
              <div className="w-full flex items-end gap-px h-full justify-center">
                <div
                  className="flex-1 bg-brand-500 hover:bg-brand-600 rounded-t transition-all relative"
                  style={{ height: `${(day.views / maxViews) * 100}%`, minHeight: '2px' }}
                  title={`${day.views} total views on ${day.date}`}
                >
                  {day.views > 0 && (
                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-semibold text-gray-600">
                      {day.views}
                    </span>
                  )}
                </div>
                <div
                  className="flex-1 bg-teal-400 hover:bg-teal-500 rounded-t transition-all relative"
                  style={{ height: `${(day.unique / maxViews) * 100}%`, minHeight: '2px' }}
                  title={`${day.unique} unique visitors on ${day.date}`}
                >
                  {day.unique > 0 && (
                    <span className="absolute -top-5 left-1/2 -translate-x-1/2 text-[10px] font-semibold text-gray-600">
                      {day.unique}
                    </span>
                  )}
                </div>
              </div>
              <span className="mt-2 text-[10px] text-gray-500 text-center whitespace-nowrap">
                {day.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
